import React, { useCallback, useEffect, useRef, useState } from 'react';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import MeetingMemberAvatar from 'flow/components/meeting/shared/MeetingMemberAvatar';
import MeetingTileAvatarLayer from 'flow/components/meeting/shared/MeetingTileAvatarLayer';
import { selectFullscreenOrNarrowWindow } from 'flow/selectors/meeting-selectors';
import {
    selectMemberIsSpeaking,
    selectVideoShowIncomingVideo,
} from 'flow/selectors/video-selectors';
import { restartReceiverPeerResetAttemptCount } from 'flow/actions/meeting-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useMeetingMemberGuestBadge from 'flow/hooks/useMeetingMemberGuestBadge';
import useVideoFrameReady from 'flow/hooks/useVideoFrameReady';
import { twMerge } from 'tailwind-merge';
import PinOverlayButton from '../overlays/PinOverlayButton';
import TileParticipantControls from '../overlays/TileParticipantControls';
import RemoteVideoLabels from '../overlays/RemoteVideoLabels';
import {
    getInnerContainerClass,
    getOuterContainerClass,
} from '../remote-video-classes';
import { ChatUserKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { VideoMember } from 'flow/types/Video';

interface WebRtcPeer {
    peerConnection?: RTCPeerConnection;
    getRemoteStream?: () => MediaStream | null;
    isEmpty?: () => boolean;
}

interface TileVideoParticipantProps {
    member: TypedMap<VideoMember>;
    isSecondary?: boolean;
    number?: string;
    /** The roster member's chatUserKey, if they are a colleague (VID-264). */
    chatUserKey?: ChatUserKey;
    memberKey?: string;
    isCompact?: boolean;
    showPinControl?: boolean;
    pinFeedName?: string;
}

type MemberStateInputs = {
    name: string | null;
    audioActive: boolean;
    videoActive: boolean;
    screenShareActive: boolean;
};

type MemberStateSetters = {
    setName: (value: string | null) => void;
    setPlayAudio: (value: boolean) => void;
    setShowVideo: (value: boolean) => void;
    setScreenShareActive: (value: boolean) => void;
    setHasRendered: (value: boolean) => void;
    resetVisibleFrame: () => void;
};

export const computeShouldShowAvatar = (
    showVideo: boolean,
    screenShareActive: boolean,
    hasVisibleFrame: boolean,
    isCameraFeed: boolean,
): boolean => {
    // What this tile is meant to show: a camera feed shows the camera, the
    // screen feed shows the shared screen. If that content is off, show the
    // avatar — e.g. a presenter muting their camera on its (secondary) feed,
    // whose screen is still active so a plain `!screenShareActive` check misses.
    const hasContentToShow = isCameraFeed ? showVideo : screenShareActive;
    if (!hasContentToShow) {
        return true;
    }
    // Show the avatar until a frame has actually decoded (VID-502) — a stream
    // being attached doesn't mean it has pixels yet.
    return !hasVisibleFrame;
};

const syncMemberState = (
    inputs: MemberStateInputs,
    remoteVideoRef: React.RefObject<HTMLVideoElement>,
    setters: MemberStateSetters,
): void => {
    const memberName = inputs.name;
    setters.setName(memberName);
    setters.setPlayAudio(inputs.audioActive);
    const { videoActive, screenShareActive } = inputs;
    if (!videoActive && !screenShareActive && remoteVideoRef.current) {
        setters.setHasRendered(false);
        setters.resetVisibleFrame();
        remoteVideoRef.current.srcObject = null;
    }
    setters.setShowVideo(videoActive);
    setters.setScreenShareActive(screenShareActive);
};

const TileVideoParticipant: React.FC<TileVideoParticipantProps> = ({
    member,
    isSecondary,
    number,
    chatUserKey,
    memberKey,
    isCompact = false,
    showPinControl = false,
    pinFeedName,
}) => {
    const dispatch = useAppDispatch();

    const isFullscreenOrNarrow = useAppSelector(selectFullscreenOrNarrowWindow);
    // These read the `member` prop, not the store — `member` is already a
    // key-based selector result passed down, so read it directly rather than
    // through useAppSelector (which would subscribe every tile to the whole
    // store and re-run on every dispatch app-wide).
    const peerSetupAttempts = member.get('peerSetupAttempts') || 0;
    const primaryPeer = member.get('primaryPeer') as WebRtcPeer | null;
    const secondaryPeer = member.get('secondaryPeer') as WebRtcPeer | null;
    const memberName = member.get('name');
    const showIncomingVideo = useAppSelector(selectVideoShowIncomingVideo);
    const memberIsSpeaking = useAppSelector((state) =>
        selectMemberIsSpeaking(state, memberName),
    );

    const remoteVideoRef = useRef<HTMLVideoElement>(null);
    const [showVideo, setShowVideo] = useState(true);
    const [screenShareActive, setScreenShareActive] = useState(false);
    const [hasRendered, setHasRendered] = useState(false);
    const { hasVisibleFrame, onLoadedData, resetVisibleFrame } =
        useVideoFrameReady();
    const [peerToUse, setPeerToUse] = useState<WebRtcPeer | null>(primaryPeer);
    const [name, setName] = useState<string | null>(null);
    const [playAudio, setPlayAudio] = useState(true);

    // Derived during render: these are pure functions of the props/state above,
    // so holding them in state (set from effects) only added a render-cycle lag.
    const isCameraFeed = isSecondary || !screenShareActive;
    // Remote camera feeds are suppressed when the viewer turns off incoming
    // video (screen shares are unaffected). Gates the imperative stream attach
    // and the avatar swap so the <video> stops decoding rather than playing
    // hidden behind the avatar.
    const cameraVideoVisible = showVideo && showIncomingVideo !== false;
    const hasContentToShow = isCameraFeed
        ? cameraVideoVisible
        : screenShareActive;
    const displayName = sliceUUIDFromUserName(name);

    const restartPrimaryReceiverPeer = useCallback(
        (name: string) => dispatch(restartReceiverPeerResetAttemptCount(name)),
        [dispatch],
    );

    useEffect(() => {
        setPeerToUse(isSecondary ? secondaryPeer : primaryPeer);
    }, [isSecondary, secondaryPeer, primaryPeer]);

    const setConnectionUpdated = useCallback(() => {
        setHasRendered(false);
    }, []);

    useEffect(() => {
        if (peerToUse?.peerConnection) {
            const rtcPeerConnection = peerToUse.peerConnection;
            if (rtcPeerConnection?.connectionState) {
                // Chrome implementation
                // Goes through: connecting, connected, disconnected, failed
                rtcPeerConnection.addEventListener(
                    'connectionstatechange',
                    setConnectionUpdated,
                );
            } else {
                // 'onconnectionstatechange' is not supported in FireFox
                // Goes through: checking, connected, disconnected, closed
                rtcPeerConnection.addEventListener(
                    'iceconnectionstatechange',
                    setConnectionUpdated,
                );
            }
        }
        return () => {
            const rtcPeerConnection = peerToUse?.peerConnection;
            if (!rtcPeerConnection) {
                return;
            }
            // Mirror the attach branch: remove whichever listener we added, or
            // Firefox (which uses iceconnectionstatechange, having no
            // connectionState) leaks a listener on every peer swap / unmount.
            if (rtcPeerConnection.connectionState) {
                rtcPeerConnection.removeEventListener(
                    'connectionstatechange',
                    setConnectionUpdated,
                );
            } else {
                rtcPeerConnection.removeEventListener(
                    'iceconnectionstatechange',
                    setConnectionUpdated,
                );
            }
        };
    }, [peerToUse, setConnectionUpdated]);

    useEffect(() => {
        if (peerToUse && hasContentToShow) {
            const hasActiveStream =
                remoteVideoRef.current?.srcObject &&
                (remoteVideoRef.current.srcObject as MediaStream)?.active;
            setHasRendered(true);
            if (
                peerToUse.getRemoteStream &&
                !hasActiveStream &&
                hasRendered &&
                remoteVideoRef.current
            ) {
                const el = remoteVideoRef.current;
                const stream = peerToUse.getRemoteStream();
                el.srcObject = stream;
                resetVisibleFrame();
            }
        }
    }, [
        peerToUse,
        hasContentToShow,
        screenShareActive,
        hasRendered,
        resetVisibleFrame,
    ]);

    useEffect(() => {
        // Detach the stream when this feed has no content to show (camera off,
        // or the viewer turned off incoming video) so it stops decoding instead
        // of playing hidden behind the avatar.
        if (!hasContentToShow && remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
            resetVisibleFrame();
            setHasRendered(false);
        }
    }, [hasContentToShow, resetVisibleFrame]);

    // Depend on the extracted fields, not the Map identity: the reducer makes a
    // new member Map on any field change (speaking activity, peer churn, …), so
    // keying the effect on `member` would re-run these setters on unrelated
    // mutations.
    const memberAudioActive = member.get('audioActive');
    const memberVideoActive = member.get('videoActive');
    const memberScreenShareActive = member.get('screenShareActive');

    useEffect(() => {
        syncMemberState(
            {
                name: memberName,
                audioActive: memberAudioActive,
                videoActive: memberVideoActive,
                screenShareActive: memberScreenShareActive,
            },
            remoteVideoRef,
            {
                setName,
                setPlayAudio,
                setShowVideo,
                setScreenShareActive,
                setHasRendered,
                resetVisibleFrame,
            },
        );
    }, [
        memberName,
        memberAudioActive,
        memberVideoActive,
        memberScreenShareActive,
        resetVisibleFrame,
    ]);

    const restartVideo = useCallback(() => {
        if (name) {
            restartPrimaryReceiverPeer(name);
        }
    }, [name, restartPrimaryReceiverPeer]);

    // One memoized selector instance per tile — creating it inside the selector
    // (the old pattern) made a fresh instance every call, defeating reselect's
    // memoization so the full contact aggregation reran on every dispatch.
    const guestBadge = useMeetingMemberGuestBadge(memberName);

    const shouldShowAvatar = computeShouldShowAvatar(
        cameraVideoVisible,
        screenShareActive,
        hasVisibleFrame,
        isCameraFeed,
    );

    const showPinButton =
        showPinControl && isCameraFeed && !isSecondary && !!pinFeedName;

    // Host controls (VID-53): shown on a participant's own camera-feed tile when
    // their mic and/or camera is on. Skipped on the shared-screen stage and the
    // secondary camera feed during a screen share so a live share isn't touched.
    const showParticipantControls =
        isCameraFeed &&
        !isSecondary &&
        !!memberName &&
        (playAudio || showVideo);

    return (
        <div className={twMerge('group', getOuterContainerClass())}>
            {showPinButton && pinFeedName && (
                <PinOverlayButton feedName={pinFeedName} />
            )}
            {showParticipantControls && (
                <TileParticipantControls
                    feedName={memberName}
                    audioActive={playAudio}
                    videoActive={showVideo}
                />
            )}
            <div className={getInnerContainerClass(isFullscreenOrNarrow)}>
                {/* Keeps its size under the avatar layer, so the tile doesn't
                    reflow when the video appears */}
                <video
                    key={memberKey}
                    ref={remoteVideoRef}
                    autoPlay={true}
                    playsInline={true}
                    className="h-full w-full object-cover"
                    muted={true}
                    onLoadedData={onLoadedData}
                />
                {shouldShowAvatar ? (
                    <MeetingTileAvatarLayer>
                        <MeetingMemberAvatar
                            hasVideo={false}
                            name={name || displayName || ''}
                            isCaller={false}
                            chatUserKey={chatUserKey}
                            size="meetingTile"
                            phoneNumber={number}
                        />
                    </MeetingTileAvatarLayer>
                ) : null}
                <RemoteVideoLabels
                    displayName={displayName}
                    guestBadge={guestBadge}
                    isCameraFeed={isCameraFeed}
                    isCaller={false}
                    playAudio={playAudio}
                    showVideo={showVideo}
                    peerSetupAttempts={peerSetupAttempts}
                    isCompact={isCompact}
                    onRestartVideo={restartVideo}
                    // Stats are collected for the primary receiver peer only,
                    // so the secondary (camera-during-screen-share) feed has
                    // none of its own to show.
                    statsMemberName={isSecondary ? null : memberName}
                />
            </div>
            {/* Active-speaker frame, drawn above the video/avatar (a ring on the
                container sits beneath them and is hidden by a camera feed). Face
                tile only — never the shared-screen stage (VID-214 AC10). */}
            {memberIsSpeaking && isCameraFeed && (
                <div className="pointer-events-none absolute inset-0 z-[2] rounded-xl border-4 border-white" />
            )}
        </div>
    );
};

// Memoized: each tile's props are stable per participant (the videoMember is a
// key-based selector result, the rest are primitives/stable callbacks), so a
// change to one member — or a feed reorder from speaking activity — no longer
// re-renders every other tile's (expensive) body. The component still re-renders
// when its own props or its own selectors change.
export default React.memo(TileVideoParticipant);
